import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RebalancingModel } from '@core/models/rebalancing-model.model';
import { RebalancesService } from '@core/services/rebalance.service';
import { FormControlFields } from '@core/utils/object-helpers';
import { AlertComponent, ButtonComponent, IconComponent } from '@shared/components';
import { EIcons } from '@shared/models';
import { MaterialModule } from '@shared/modules';
import { Subscription } from 'rxjs';
import { SecondStepForm } from '../interface/forms.interface';

@Component({
  selector: 'app-second-step',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, ButtonComponent, IconComponent],
  templateUrl: './second-step.component.html',
  styleUrl: './second-step.component.scss'
})
export class SecondStepComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly rebalancingsServices = inject(RebalancesService);
  readonly #subscriptions = new Subscription();
  readonly EIcons = EIcons;

  form: FormGroup<FormControlFields<SecondStepForm>>;

  oldDate = new Date();
  @Input() rebalancingId!: number
  @Input() currentStep = signal<number>(-1);

  file = signal<File | null>(null);
  previewImage = signal<string | ArrayBuffer | null>('');
  @Input() rebalance: RebalancingModel | null = null;

  constructor() {
    this.form = new FormGroup<FormControlFields<SecondStepForm>>({
      plate: new FormControl(''),
      vin: new FormControl(''),
      acountable: new FormControl('', { validators: [Validators.required, Validators.pattern('^[A-Za-zÀ-ÖØ-öø-ÿ\\s]+$')] }),
      date: new FormControl(new Date(), { validators: [Validators.required] }),
      hour: new FormControl('', { validators: [Validators.required, this.hourValidator()] }),
      soc_zero: new FormControl(true),
      obs: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.#subscriptions.add(
      this.form.controls.date.valueChanges.subscribe(() => {
        this.form.controls.hour.updateValueAndValidity();
      }),
    );

    this.#setForm()
  }

  #setForm() {
    this.form.controls.plate?.enable();
    this.form.controls.vin?.enable();

    this.form.controls.plate?.setValue(this.rebalance!.unit.plate);
    this.form.controls.vin?.setValue(this.rebalance!.unit.vin);

    this.form.controls.plate?.disable();
    this.form.controls.vin?.disable();
  }

  ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();
  }

  save() {
    let data: any;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const secondForm = this.form.value;
    delete secondForm.acountable, secondForm.obs;

    data = {
      data: secondForm,
      step: 2,
      responsible: this.form.controls.acountable.value,
      comments: this.form.controls.obs?.value
    };

    this.form.disable();

    const extensionIndex = this.file()?.name.lastIndexOf('.');
    const extension = this.file()?.name.slice(extensionIndex);
    const fileName = `reb_${this.rebalance!.id}_${data.step + 1}_${new Date().getTime()}${extension}`;
    const newFile = new File([this.file()!], fileName, {
      type: this.file()?.type,
      lastModified: this.file()?.lastModified,
    });

    this.rebalancingsServices.sendRebalancingData(JSON.stringify(data), this.rebalance!.id, newFile).subscribe({
      next: () => {
        this.dialog.open(AlertComponent, {
          data: {
            type: 'success',
            message: 'Rebalanceo guardado',
            successButtonText: 'Aceptar',
          },
        }).afterClosed().subscribe(() => {
          this.currentStep.set(3);
        });
      },
      error: err => {
        console.log(err.error.error);
      },
    });

  }

  secondFormHasError(control: string, error: string): boolean | undefined {
    return this.form.get(control)?.touched && this.form.get(control)?.hasError(error);
  }

  resetRebalancing() {
    const dialogConfirm = this.dialog.open(AlertComponent, {
      data: {
        type: 'success',
        message: '¿Desea anular el rebalanceo?',
        successButtonText: 'Aceptar',
        hasCancelButton: true
      },
    });

    dialogConfirm.afterClosed().subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.rebalancingsServices.resetRebalancing(this.rebalance!.id).subscribe(
          () => {
            const dialog = this.dialog.open(AlertComponent, {
              data: {
                type: 'success',
                message: 'Rebalanceo anulado con éxito',
                successButtonText: 'Aceptar',
              },
            });

            dialog.afterClosed().subscribe(() => {
              this.router.navigate(['/page/mex/services-and-repairs/rebalancings']);
            });
          }, err => {
            console.log(err.error);

            this.dialog.open(AlertComponent, {
              data: {
                type: 'error',
                message: 'Error al anular rebalanceo',
                successButtonText: 'Aceptar',
              },
            });
          },
        );
      }
    });
  }

  parseFileToURL(file: File | any) {
    if (file.url) {
      this.previewImage.set(file.url);
      return;
    }

    return new Promise((resolve, reject) => {
      const lector = new FileReader();

      lector.onload = (event) => {
        this.previewImage.set(event.target!.result);
        resolve(event.target!.result);
      };

      lector.onerror = (error) => {
        console.log(error);
        reject(new Error('Error al leer el archivo.'));
      };

      lector.readAsDataURL(file);
    });
  }

  async setFiles(file: any, selector: any) {
    file.preventDefault();

    if (file.target.files[0]) {
      this.file.set(file.target.files[0]);
    }

    await this.parseFileToURL(this.file());

    selector.value = '';
  }

  private hourValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value && this.form && this.form.controls.date.value) {
        return null;
      }

      if (!this.rebalance) {
        return null;
      }

      const newDate = new Date(this.form.controls.date.value!);
      const [newHour, newMin] = control.value.split(':') as [string, string];
      newDate.setHours(parseInt(newHour), parseInt(newMin));

      const oldDate = new Date(this.rebalance.steps[0].data.date);
      const [oldHour, oldMin] = this.rebalance.steps[0].data.hour.split(':') as [string, string];
      oldDate.setHours(parseInt(oldHour), parseInt(oldMin));
      
      if (oldDate.getTime() > newDate.getTime()) {
        return { errorHour: true };
      }
      return null;
    };
  }
}
